/**
 * Habit Card — sticky left column in the grid.
 *
 * Layout:
 *   - Left: emoji in circle with SVG progress ring
 *   - Right: name row (+ horizontal dots), optional challenge row, optional details row
 *
 * Height (5px padding):
 *   - Name only:                36 + 5*2 = 46px
 *   - Name + challenge:    20+2+18 + 5*2 = 50px
 *   - Name + challenge + details: 20+2+18+2+16 + 5*2 = 68px
 *   - Name + details (no challenge): 20+2+16 + 5*2 = 48px
 *
 * Row heights: name=20px, challenge=18px, details=16px, gap=2px
 */

import React, { useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  MoreHorizontal, Pencil, Trash2, Flame,
  Clock4, Hourglass, Swords, Target,
} from 'lucide-react'
import { useDeleteHabit } from '@turnifly/shared/hooks'
import { useHomeStore } from '@turnifly/shared/stores'
import type { MonthlyHabitItem } from '@turnifly/shared/types'

/* ── Height calculation ── */

const PAD = 5
const NAME_H = 20
const CHALLENGE_H = 18
const DETAILS_H = 16
const GAP = 2

function hasDetails(habit: MonthlyHabitItem): boolean {
  return !!(
    habit.time_start ||
    (habit.time_duration && habit.time_duration > 0) ||
    (habit.value_required && habit.value_target)
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function getHabitCardHeight(habit: MonthlyHabitItem): number {
  const hasCh = !!habit.challenge_id
  const hasDt = hasDetails(habit)

  if (!hasCh && !hasDt) {
    // name only: emoji dictates height
    return 36 + PAD * 2 // 46px
  }
  // content height = name + gaps + optional rows
  let content = NAME_H
  if (hasCh) content += GAP + CHALLENGE_H
  if (hasDt) content += GAP + DETAILS_H
  return content + PAD * 2
}

/* ── Secondary text color ── */
const SEC = '#768c9e'

interface HabitCardProps {
  habit: MonthlyHabitItem
}

export const HabitCard: React.FC<HabitCardProps> = React.memo(({ habit }) => {
  const { t, i18n } = useTranslation()
  const setEditingHabit = useHomeStore((s) => s.setEditingHabit)
  const setViewingHabitId = useHomeStore((s) => s.setViewingHabitId)
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 })
  const btnRef = useRef<HTMLButtonElement>(null)
  const deleteMutation = useDeleteHabit()

  const currentStreak = habit.streak.current
  const cardH = getHabitCardHeight(habit)
  const showDetails = hasDetails(habit)
  const isChallengeHabit = !!habit.challenge_id

  const openMenu = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation()
    if (isChallengeHabit) return // Challenge habits cannot be edited/deleted from home
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setMenuPos({ top: rect.bottom + 4, left: rect.right - 140 })
    }
    setMenuOpen(true)
  }, [isChallengeHabit])

  const closeMenu = useCallback((): void => setMenuOpen(false), [])

  const handleDelete = useCallback((): void => {
    closeMenu()
    deleteMutation.mutate(habit.id)
  }, [habit.id, closeMenu, deleteMutation])

  const handleEdit = useCallback((): void => {
    closeMenu()
    setEditingHabit(habit)
  }, [closeMenu, setEditingHabit, habit])

  const timeStart = habit.time_start ? habit.time_start.slice(0, 5) : null
  const timeDuration = habit.time_duration

  return (
    <div
      className="relative bg-[var(--color-bg)]"
      style={{ height: `${cardH}px`, padding: '0 3px 3px 0' }}
    >
      <div
        className="h-full flex flex-col justify-center bg-glass-solid rounded-lg"
        style={{ padding: `${PAD}px 5px ${PAD - 3}px 8px` }}
      >
      {/* ── Content (emoji is in separate sticky column now) ── */}
      <div className="flex-1 min-w-0 flex flex-col justify-center" style={{ gap: `${GAP}px` }}>
        {/* Name row: 20px height, 12px semibold white */}
        <div className="flex items-center gap-1 min-w-0" style={{ height: `${NAME_H}px` }}>
          {isChallengeHabit && <span className="shrink-0 text-[11px] select-none">🏆</span>}
          <button
            onClick={() => setViewingHabitId(habit.id)}
            className="text-[12px] font-semibold text-white truncate leading-none font-display flex-1 min-w-0 text-left tap-highlight-transparent hover:text-primary/90 transition-colors"
          >
            {habit.name}
          </button>
          {currentStreak > 0 && (
            <span className="shrink-0 inline-flex items-center gap-px text-[9px] font-bold text-streak">
              <Flame size={9} fill="currentColor" />{currentStreak}
            </span>
          )}
          <button
            ref={btnRef}
            onClick={openMenu}
            className="shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-white/[0.06] transition-colors tap-highlight-transparent"
            style={{ color: SEC }}
          >
            <MoreHorizontal size={13} />
          </button>
        </div>

        {/* Challenge row: 18px height, 11px semibold #768c9e */}
        {habit.challenge_id && (
          <div className="flex items-center gap-1 min-w-0" style={{ height: `${CHALLENGE_H}px` }}>
            <Swords size={11} className="shrink-0" style={{ color: 'var(--color-accent)' }} fill="currentColor" />
            <span className="text-[11px] font-semibold truncate" style={{ color: SEC }}>
              {t('home.challenge')}
            </span>
          </div>
        )}

        {/* Details row: 16px height, 10px #768c9e */}
        {showDetails && (
          <div className="flex items-center gap-2 min-w-0" style={{ height: `${DETAILS_H}px` }}>
            {timeStart && (
              <span className="shrink-0 inline-flex items-center gap-1">
                <Clock4 size={10} style={{ color: 'var(--color-primary)' }} fill="currentColor" stroke="var(--color-bg)" />
                <span className="text-[10px]" style={{ color: SEC }}>{timeStart}</span>
              </span>
            )}
            {timeDuration != null && timeDuration > 0 && (
              <span className="shrink-0 inline-flex items-center gap-1">
                <Hourglass size={10} style={{ color: 'var(--color-warning)' }} fill="currentColor" />
                <span className="text-[10px]" style={{ color: SEC }}>
                  {timeDuration}{i18n.language === 'uz' ? ' daq' : ' min'}
                </span>
              </span>
            )}
            {habit.value_required && habit.value_target && (
              <span className="shrink-0 inline-flex items-center gap-1">
                <Target size={10} style={{ color: 'var(--color-success)' }} fill="currentColor" stroke="var(--color-bg)" />
                <span className="text-[10px]" style={{ color: SEC }}>
                  {habit.value_target}{habit.value_unit ? ` ${habit.value_unit}` : ''}
                </span>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Portal dropdown ── */}
      {menuOpen && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={closeMenu} />
          <div
            className="fixed z-[9999] bg-surface border border-white/[0.08] rounded-lg overflow-hidden min-w-[140px] shadow-xl"
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            <button
              onClick={handleEdit}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-text hover:bg-white/[0.04] transition-colors"
            >
              <Pencil size={13} className="text-text-hint/60" />{t('common.edit')}
            </button>
            <div className="h-px bg-white/[0.06] mx-2" />
            <button
              onClick={handleDelete}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-danger hover:bg-white/[0.04] transition-colors"
            >
              <Trash2 size={13} />{t('common.delete')}
            </button>
          </div>
        </>,
        document.body,
      )}
      </div>
    </div>
  )
})

HabitCard.displayName = 'HabitCard'
